import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit
} from 'firebase/firestore';
import { db } from './firebase';
import { CompleteKundali } from '../types/kundali';
import { UserProfile, UserRole, AccountTier, BanRecord } from '../types/user';
import { recordAuditLog } from './authorizationEngine';

export interface StoredKundaliRecord {
  id: string;
  userId: string;
  userEmail: string;
  createdByName: string;
  name: string;
  gender: string;
  date: string;
  time: string;
  placeName: string;
  latitude: number;
  longitude: number;
  timezone: number;
  chartStyle: string;
  tags: string[];
  notes: string;
  isPublic: boolean;
  createdAt: string;
  updatedAt: string;
  lastAccessedAt: string;
  kundaliData: CompleteKundali;
}

const COLLECTION_NAME = 'kundalis';

// Save or Update a Kundali in Firestore
export async function saveKundaliToCloud(
  kundali: CompleteKundali,
  user: UserProfile,
  existingId?: string,
  tags: string[] = ['Saved'],
  notes: string = '',
  isPublic: boolean = false,
  originalName?: string
): Promise<StoredKundaliRecord> {
  if (!user || !user.uid) {
    throw new Error('User authentication is required to save a Kundali to the cloud.');
  }
  let targetId = existingId || kundali.id || kundali.cloudId;
  const normalizedTargetName = (kundali.birthDetails.name || '').toLowerCase().trim();
  const normalizedOriginalName = (originalName || '').toLowerCase().trim();
  let existingCreatedAt = new Date().toISOString();

  // If no explicit ID provided, check for an existing document with matching name or original name for this user
  if (!targetId && user?.uid && (normalizedTargetName || normalizedOriginalName)) {
    try {
      const q = query(
        collection(db, COLLECTION_NAME),
        where('userId', '==', user.uid)
      );
      const snap = await getDocs(q);
      const match = snap.docs.find((d) => {
        const data = d.data();
        const docName = (data.name || '').toLowerCase().trim();
        if (normalizedOriginalName && docName === normalizedOriginalName) return true;
        if (normalizedTargetName && docName === normalizedTargetName) return true;
        return false;
      });
      if (match) {
        targetId = match.id;
        const matchData = match.data() as StoredKundaliRecord;
        if (matchData.createdAt) {
          existingCreatedAt = matchData.createdAt;
        }
        if ((!tags || tags.length === 0 || (tags.length === 1 && tags[0] === 'Saved')) && matchData.tags) {
          tags = matchData.tags;
        }
        if (!notes && matchData.notes) {
          notes = matchData.notes;
        }
      }
    } catch (err) {
      console.warn('Could not query existing chart by name for update:', err);
    }
  }

  const id = targetId || `knd_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`;
  kundali.id = id;
  kundali.cloudId = id;
  const docRef = doc(db, COLLECTION_NAME, id);

  const record: StoredKundaliRecord = {
    id,
    userId: user.uid,
    userEmail: user.email,
    createdByName: user.displayName || user.email,
    name: kundali.birthDetails.name,
    gender: kundali.birthDetails.gender,
    date: kundali.birthDetails.date,
    time: kundali.birthDetails.time,
    placeName: kundali.birthDetails.placeName,
    latitude: kundali.birthDetails.latitude,
    longitude: kundali.birthDetails.longitude,
    timezone: kundali.birthDetails.timezoneOffsetMinutes,
    chartStyle: 'north',
    tags,
    notes,
    isPublic,
    createdAt: existingCreatedAt,
    updatedAt: new Date().toISOString(),
    lastAccessedAt: new Date().toISOString(),
    kundaliData: kundali
  };

  // Sanitize nested undefined values to avoid Firestore crashing with invalid data error
  const sanitizedRecord = JSON.parse(JSON.stringify(record));

  await setDoc(docRef, sanitizedRecord, { merge: true });
  return record;
}

// Fetch Kundalis strictly for current user
export async function getUserSavedKundalis(userId: string): Promise<StoredKundaliRecord[]> {
  if (!userId) return [];
  try {
    const q = query(
      collection(db, COLLECTION_NAME),
      where('userId', '==', userId),
      orderBy('lastAccessedAt', 'desc')
    );
    const snap = await getDocs(q);
    const results: StoredKundaliRecord[] = [];
    snap.forEach((docSnap) => {
      results.push(docSnap.data() as StoredKundaliRecord);
    });
    return results;
  } catch (err) {
    console.warn('Ordered query fallback notice, attempting unindexed query with in-memory sort:', err);
    try {
      const fallbackQ = query(
        collection(db, COLLECTION_NAME),
        where('userId', '==', userId)
      );
      const snap = await getDocs(fallbackQ);
      const results: StoredKundaliRecord[] = [];
      snap.forEach((docSnap) => {
        results.push(docSnap.data() as StoredKundaliRecord);
      });
      return results.sort((a, b) => new Date(b.lastAccessedAt || 0).getTime() - new Date(a.lastAccessedAt || 0).getTime());
    } catch (fallbackErr) {
      console.error('Error fetching user kundalis:', fallbackErr);
      return [];
    }
  }
}

// ADMIN FUNCTION: Fetch ALL Kundalis across all users in the system
export async function getAllKundalisAdmin(): Promise<StoredKundaliRecord[]> {
  try {
    const q = query(collection(db, COLLECTION_NAME), orderBy('lastAccessedAt', 'desc'));
    const snap = await getDocs(q);
    const results: StoredKundaliRecord[] = [];
    snap.forEach((docSnap) => {
      results.push(docSnap.data() as StoredKundaliRecord);
    });
    return results;
  } catch (err) {
    console.error('Error fetching all admin kundalis:', err);
    return [];
  }
}

// Delete a Kundali document
export async function deleteKundaliFromCloud(id: string): Promise<void> {
  await deleteDoc(doc(db, COLLECTION_NAME, id));
}

// Update Kundali Notes, Tags, or Visibility
export async function updateKundaliDetails(
  id: string,
  updates: Partial<{ notes: string; tags: string[]; isPublic: boolean }>
): Promise<void> {
  const docRef = doc(db, COLLECTION_NAME, id);
  await updateDoc(docRef, {
    ...updates,
    updatedAt: new Date().toISOString()
  });
}

// Track access for frequency sorting
export async function trackKundaliAccess(id: string): Promise<void> {
  const docRef = doc(db, COLLECTION_NAME, id);
  await updateDoc(docRef, {
    lastAccessedAt: new Date().toISOString()
  });
}

// Recalculate full astrological data for all existing charts using updated engine
export async function recalculateAllSavedKundalisInCloud(
  recalculateFn: (details: any) => CompleteKundali
): Promise<{ updatedCount: number; errors: number }> {
  let updatedCount = 0;
  let errors = 0;

  try {
    const snap = await getDocs(collection(db, COLLECTION_NAME));
    for (const docSnap of snap.docs) {
      try {
        const record = docSnap.data() as StoredKundaliRecord;
        if (record && record.kundaliData && record.kundaliData.birthDetails) {
          const freshKundali = recalculateFn(record.kundaliData.birthDetails);
          const docRef = doc(db, COLLECTION_NAME, docSnap.id);
          await updateDoc(docRef, {
            kundaliData: freshKundali,
            updatedAt: new Date().toISOString()
          });
          updatedCount++;
        }
      } catch (e) {
        console.error(`Failed recalculating chart document ${docSnap.id}:`, e);
        errors++;
      }
    }
  } catch (err) {
    console.error('Failed fetching collection for mass recalculation:', err);
    throw err;
  }

  return { updatedCount, errors };
}

// Helper to construct authorization headers for administrative endpoints
function getAdminAuthHeaders(actorUser?: UserProfile): Record<string, string> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  let token = (actorUser as any)?.sessionToken || (actorUser as any)?.token || actorUser?.uid;
  if (!token) {
    try {
      const stored = localStorage.getItem('astro_custom_session');
      if (stored) {
        const parsed = JSON.parse(stored);
        token = parsed?.sessionToken || parsed?.token || parsed?.uid;
      }
    } catch {}
  }
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

// ADMIN FUNCTION: Fetch all registered users
export async function fetchAllUsersAdmin(actorUser?: UserProfile): Promise<UserProfile[]> {
  const userMap = new Map<string, UserProfile>();

  // 1. Fetch from server endpoint with authorization header
  try {
    const res = await fetch('/api/admin/users', {
      headers: getAdminAuthHeaders(actorUser)
    });
    if (res.ok) {
      const serverUsers: UserProfile[] = await res.json();
      serverUsers.forEach((u) => {
        if (u && u.uid) userMap.set(u.uid, u);
      });
    }
  } catch (err) {
    console.warn('Failed fetching users from server store:', err);
  }

  // 2. Fetch from Firestore users collection
  try {
    const snap = await getDocs(collection(db, 'users'));
    snap.forEach((docSnap) => {
      const u = docSnap.data() as UserProfile;
      if (u && u.uid) {
        userMap.set(u.uid, { ...userMap.get(u.uid), ...u });
      }
    });
  } catch (err) {
    console.warn('Error fetching users from Firestore:', err);
  }

  return Array.from(userMap.values()).sort(
    (a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime()
  );
}

// ADMIN FUNCTION: Update user role and entitlements
export async function updateUserRoleAdmin(
  targetUid: string,
  newRole: UserRole | string,
  newTier?: AccountTier | string,
  entitlements?: { hasPracticeEntitlement?: boolean; hasConsultationEntitlement?: boolean; kundaliQuota?: number },
  actorUser?: UserProfile
): Promise<void> {
  // 1. Update on server endpoint
  try {
    await fetch('/api/admin/update-role', {
      method: 'POST',
      headers: getAdminAuthHeaders(actorUser),
      body: JSON.stringify({ uid: targetUid, newRole, newTier, entitlements })
    });
  } catch (e) {
    console.warn('Backend store role update warning:', e);
  }

  // 2. Update in Firestore if accessible
  try {
    const docRef = doc(db, 'users', targetUid);
    const updates: any = {
      role: newRole,
      updatedAt: new Date().toISOString()
    };
    if (newTier) updates.tier = newTier;
    if (entitlements?.hasPracticeEntitlement !== undefined) {
      updates.hasPracticeEntitlement = entitlements.hasPracticeEntitlement;
    }
    if (entitlements?.hasConsultationEntitlement !== undefined) {
      updates.hasConsultationEntitlement = entitlements.hasConsultationEntitlement;
    }
    if (entitlements?.kundaliQuota !== undefined) {
      updates.kundaliQuota = Number(entitlements.kundaliQuota);
    }
    await updateDoc(docRef, updates);

    recordAuditLog({
      eventType: 'ROLE_CHANGE',
      user: actorUser || null,
      resourceType: 'user',
      resourceId: targetUid,
      action: 'UPDATE_ROLE_TIER',
      decision: 'ALLOW',
      details: `Updated user [${targetUid}] role to [${newRole}], Tier: [${newTier || 'unchanged'}]`
    });
  } catch (err) {
    console.warn('Firestore user role update warning:', err);
  }
}

// ADMIN FUNCTION: Reset Password for a user account
export async function resetUserPasswordAdmin(
  targetUid: string,
  newPassword: string,
  actorUser?: UserProfile
): Promise<void> {
  // 1. Update on server endpoint
  try {
    await fetch('/api/admin/reset-password', {
      method: 'POST',
      headers: getAdminAuthHeaders(actorUser),
      body: JSON.stringify({ uid: targetUid, newPassword })
    });
  } catch (e) {
    console.warn('Backend store password sync warning:', e);
  }

  // 2. Update in Firestore if accessible
  try {
    const docRef = doc(db, 'users', targetUid);
    await updateDoc(docRef, {
      passwordResetAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    });
  } catch (err) {
    console.warn('Firestore user password reset warning:', err);
  }
}

// ADMIN FUNCTION: Delete a User Account
export async function deleteUserAccountAdmin(targetUid: string, actorUser?: UserProfile): Promise<void> {
  let serverSuccess = false;

  // 1. First delete from backend server memory store
  try {
    const res = await fetch('/api/admin/delete-user', {
      method: 'POST',
      headers: getAdminAuthHeaders(actorUser),
      body: JSON.stringify({ uid: targetUid })
    });
    if (res.ok) serverSuccess = true;
  } catch (e) {
    console.warn('Backend store delete user warning:', e);
  }

  // 2. Delete from Firestore if doc exists / permissions allow
  try {
    const docRef = doc(db, 'users', targetUid);
    await deleteDoc(docRef);
  } catch (err) {
    console.warn('Firestore delete user warning (permission or non-existent doc):', err);
    if (!serverSuccess) {
      // If both server and Firestore failed, rethrow to notify UI
      throw err;
    }
  }
}

// Reassign specific named Kundalis to the current profile and remove duplicates from all other profiles
export async function cleanupAndReassignNamedKundalis(currentUser: UserProfile): Promise<void> {
  const TARGET_NAMES = ['Bijender', 'Pawas', 'Navratan', 'Kashika', 'Matrika'].map(n => n.toLowerCase());
  try {
    const allRecords = await getAllKundalisAdmin();
    const matchingRecords = allRecords.filter(r => r.name && TARGET_NAMES.includes(r.name.toLowerCase().trim()));
    
    if (matchingRecords.length === 0) return;

    const currentUserRecords = matchingRecords.filter(r => r.userId === currentUser.uid);
    const currentUserNames = new Set(currentUserRecords.map(r => r.name.toLowerCase().trim()));

    for (const record of matchingRecords) {
      const normalizedName = record.name.toLowerCase().trim();
      
      if (record.userId !== currentUser.uid) {
        if (!currentUserNames.has(normalizedName)) {
          const docRef = doc(db, COLLECTION_NAME, record.id);
          await updateDoc(docRef, {
            userId: currentUser.uid,
            userEmail: currentUser.email,
            createdByName: currentUser.displayName || currentUser.email,
            updatedAt: new Date().toISOString()
          });
          currentUserNames.add(normalizedName);
          console.log(`Reassigned chart ${record.name} (${record.id}) to ${currentUser.email}`);
        } else {
          const docRef = doc(db, COLLECTION_NAME, record.id);
          await deleteDoc(docRef);
          console.log(`Deleted duplicate chart ${record.name} (${record.id}) belonging to user ID ${record.userId}`);
        }
      }
    }
  } catch (err) {
    console.error('Error running named kundalis cleanup/reassignment:', err);
  }
}

// ADMIN FUNCTION: Fetch All Banned Entities / Blacklisted Identifiers
export async function fetchBannedEntitiesAdmin(actorUser?: UserProfile): Promise<BanRecord[]> {
  const banMap = new Map<string, BanRecord>();

  // 1. Fetch from server endpoint
  try {
    const res = await fetch('/api/admin/bans', {
      headers: getAdminAuthHeaders(actorUser)
    });
    if (res.ok) {
      const serverBans: BanRecord[] = await res.json();
      serverBans.forEach((b) => {
        const key = b.userId || b.email || b.phone || b.bannedAt;
        banMap.set(key, b);
      });
    }
  } catch (e) {
    console.warn('Backend store fetch bans warning:', e);
  }

  // 2. Fetch from Firestore banned_entities
  try {
    const snap = await getDocs(collection(db, 'banned_entities'));
    snap.forEach((docSnap) => {
      const b = docSnap.data() as BanRecord;
      if (b && (b.email || b.phone || b.userId)) {
        const key = b.userId || b.email || b.phone || docSnap.id;
        banMap.set(key, { ...banMap.get(key), ...b });
      }
    });
  } catch (err) {
    console.warn('Firestore fetch banned_entities warning:', err);
  }

  return Array.from(banMap.values()).sort(
    (a, b) => new Date(b.bannedAt || 0).getTime() - new Date(a.bannedAt || 0).getTime()
  );
}

// ADMIN FUNCTION: Ban User Account & Blacklist Phone/Email
export async function banUserAdmin(
  userId: string,
  email: string,
  phone: string | undefined,
  reason: string,
  bannedBy: string = 'super_admin',
  actorUser?: UserProfile
): Promise<void> {
  const cleanEmail = (email || '').trim().toLowerCase();
  const cleanPhone = phone ? phone.replace(/\D/g, '') : undefined;

  // 1. Call Backend Server
  try {
    const res = await fetch('/api/admin/ban-user', {
      method: 'POST',
      headers: getAdminAuthHeaders(actorUser),
      body: JSON.stringify({ userId, email: cleanEmail, phone: cleanPhone, reason, bannedBy })
    });
    if (!res.ok) {
      const errData = await res.json();
      throw new Error(errData.error || 'Server ban enforcement failed');
    }
  } catch (e) {
    console.warn('Backend ban sync warning:', e);
  }

  // 2. Persist to Firestore
  try {
    const bannedAt = new Date().toISOString();
    const banRecord: BanRecord = {
      userId,
      email: cleanEmail,
      phone: cleanPhone,
      bannedAt,
      bannedBy,
      reason: reason.trim(),
      isBanned: true
    };

    const banDocId = userId ? `ban_${userId}` : `ban_${Date.now()}`;
    await setDoc(doc(db, 'banned_entities', banDocId), banRecord, { merge: true });

    if (userId) {
      await updateDoc(doc(db, 'users', userId), {
        isBanned: true,
        bannedAt,
        bannedBy,
        banReason: reason.trim(),
        updatedAt: bannedAt
      });
    }

    recordAuditLog({
      eventType: 'USER_BANNED',
      user: actorUser || null,
      resourceType: 'user',
      resourceId: userId || cleanEmail,
      action: 'BAN_USER_AND_BLACKLIST_IDENTIFIERS',
      decision: 'ALLOW',
      details: `Super Admin enforced ban on [${userId || cleanEmail}]. Reason: ${reason}`
    });
  } catch (err) {
    console.warn('Firestore ban execution warning:', err);
  }
}

// ADMIN FUNCTION: Lift Ban / Restore Account
export async function unbanUserAdmin(
  userId: string,
  email?: string,
  phone?: string,
  unbannedBy: string = 'super_admin',
  actorUser?: UserProfile
): Promise<void> {
  // 1. Backend Server Call
  try {
    const res = await fetch('/api/admin/unban-user', {
      method: 'POST',
      headers: getAdminAuthHeaders(actorUser),
      body: JSON.stringify({ userId, email, phone, unbannedBy })
    });
    if (!res.ok) {
      const errData = await res.json();
      throw new Error(errData.error || 'Server unban failed');
    }
  } catch (e) {
    console.warn('Backend unban sync warning:', e);
  }

  // 2. Firestore Sync
  try {
    const banDocId = userId ? `ban_${userId}` : undefined;
    if (banDocId) {
      await updateDoc(doc(db, 'banned_entities', banDocId), {
        isBanned: false,
        unbannedAt: new Date().toISOString()
      });
    }

    if (userId) {
      await updateDoc(doc(db, 'users', userId), {
        isBanned: false,
        banReason: '',
        bannedAt: '',
        bannedBy: '',
        updatedAt: new Date().toISOString()
      });
    }

    recordAuditLog({
      eventType: 'USER_UNBANNED',
      user: actorUser || null,
      resourceType: 'user',
      resourceId: userId || email || 'unknown',
      action: 'LIFT_USER_BAN',
      decision: 'ALLOW',
      details: `Ban lifted by Super Admin for user [${userId || email}]`
    });
  } catch (err) {
    console.warn('Firestore unban execution warning:', err);
  }
}

// ADMIN FUNCTION: Preemptively Blacklist an Email or Mobile Phone
export async function blacklistIdentifierAdmin(
  identifier: string,
  type: 'email' | 'phone',
  reason: string,
  bannedBy: string = 'super_admin',
  actorUser?: UserProfile
): Promise<void> {
  // 1. Backend Server Call
  try {
    const res = await fetch('/api/admin/blacklist-identifier', {
      method: 'POST',
      headers: getAdminAuthHeaders(actorUser),
      body: JSON.stringify({ identifier, type, reason, bannedBy })
    });
    if (!res.ok) {
      const errData = await res.json();
      throw new Error(errData.error || 'Server blacklist failed');
    }
  } catch (e) {
    console.warn('Backend blacklist sync warning:', e);
  }

  // 2. Firestore Sync
  try {
    const cleanId = identifier.trim().toLowerCase();
    const docId = `blacklist_${type}_${cleanId.replace(/[^a-z0-9]/gi, '_')}`;
    const bannedAt = new Date().toISOString();

    await setDoc(doc(db, 'banned_entities', docId), {
      userId: '',
      email: type === 'email' ? cleanId : '',
      phone: type === 'phone' ? cleanId.replace(/\D/g, '') : undefined,
      bannedAt,
      bannedBy,
      reason: reason.trim(),
      isBanned: true
    }, { merge: true });

    recordAuditLog({
      eventType: 'IDENTIFIER_BLACKLISTED',
      user: actorUser || null,
      resourceType: 'system',
      resourceId: cleanId,
      action: 'PREEMPTIVE_IDENTIFIER_BLACKLIST',
      decision: 'ALLOW',
      details: `Blacklisted identifier [${cleanId}] (${type}). Reason: ${reason}`
    });
  } catch (err) {
    console.warn('Firestore blacklist execution warning:', err);
  }
}
